import { useState, useEffect } from 'react';
import { apiFetch } from './shared';

const API = '/api';

// ─── SVG-Chart für Watt / Herzfrequenz-Verlauf ───────────────────────────────
function StreamSVG({ time, watts, heartrate }) {
  const W = 580, H = 190;
  const PAD = { left: 38, right: 36, top: 8, bottom: 20 };
  const cw = W - PAD.left - PAD.right;
  const ch = H - PAD.top - PAD.bottom;

  const n = time.length;
  const step = Math.max(1, Math.floor(n / 400));
  const idx = Array.from({ length: Math.ceil(n / step) }, (_, i) => Math.min(i * step, n - 1));

  const tMin = time[0], tMax = time[n - 1];
  const xOf = t => PAD.left + ((t - tMin) / (tMax - tMin)) * cw;

  // Watts
  const wArr = watts ? idx.map(i => watts[i]).filter(v => v != null) : [];
  const hasW = wArr.length > 0;
  const wMin = hasW ? Math.max(0, Math.min(...wArr) - 10) : 0;
  const wMax = hasW ? Math.max(...wArr) + 10 : 400;
  const yOfW = v => PAD.top + ch - ((v - wMin) / (wMax - wMin)) * ch;

  // HR
  const hrArr = heartrate ? idx.map(i => heartrate[i]).filter(v => v != null) : [];
  const hasHR = hrArr.length > 0;
  const hrMin = hasHR ? Math.max(40, Math.min(...hrArr) - 5) : 60;
  const hrMax = hasHR ? Math.max(...hrArr) + 5 : 200;
  const yOfHR = v => PAD.top + ch - ((v - hrMin) / (hrMax - hrMin)) * ch;

  const wPoints = hasW
    ? idx.filter(i => watts[i] != null).map(i => `${xOf(time[i]).toFixed(1)},${yOfW(watts[i]).toFixed(1)}`).join(' ')
    : '';
  const hrPoints = hasHR
    ? idx.filter(i => heartrate[i] != null).map(i => `${xOf(time[i]).toFixed(1)},${yOfHR(heartrate[i]).toFixed(1)}`).join(' ')
    : '';

  // X-Achse: Ticks alle 10 / 20 / 30 min
  const totalMin = Math.floor(tMax / 60);
  const tickInt = totalMin <= 60 ? 10 : totalMin <= 120 ? 20 : 30;
  const xTicks = [];
  for (let m = 0; m <= totalMin; m += tickInt) xTicks.push({ m, x: xOf(m * 60) });

  // Y-Ticks Watt
  const wStep = Math.max(50, Math.ceil((wMax - wMin) / 4 / 50) * 50);
  const wTicks = [];
  for (let w = Math.ceil(wMin / wStep) * wStep; w <= wMax; w += wStep) wTicks.push({ v: w, y: yOfW(w) });

  // Y-Ticks HR
  const hrStep = Math.max(10, Math.ceil((hrMax - hrMin) / 4 / 10) * 10);
  const hrTicks = [];
  for (let h = Math.ceil(hrMin / hrStep) * hrStep; h <= hrMax; h += hrStep) hrTicks.push({ v: h, y: yOfHR(h) });

  const gridTicks = hasHR ? hrTicks : wTicks;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }}>
      <rect width={W} height={H} fill="#0d0d0d" />
      <rect x={PAD.left} y={PAD.top} width={cw} height={ch} fill="#111" />

      {/* Grid */}
      {gridTicks.map(t => (
        <line key={t.v} x1={PAD.left} x2={PAD.left + cw} y1={t.y} y2={t.y}
          stroke="#1c1c1c" strokeWidth="0.8" />
      ))}

      {/* HR-Linie */}
      {hrPoints && (
        <polyline points={hrPoints} fill="none" stroke="#c17f24"
          strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" opacity="0.9" />
      )}

      {/* Watt-Linie */}
      {wPoints && (
        <polyline points={wPoints} fill="none" stroke="#5a8fd4"
          strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" opacity="0.9" />
      )}

      {/* X-Achse */}
      {xTicks.map(t => (
        <text key={t.m} x={t.x} y={H - 3} textAnchor="middle" fontSize="8" fill="#444">
          {t.m}′
        </text>
      ))}

      {/* Linke Y-Achse: Watt */}
      {hasW && wTicks.map(t => (
        <text key={t.v} x={PAD.left - 3} y={t.y + 3} textAnchor="end" fontSize="8" fill="#5a8fd4">
          {t.v}
        </text>
      ))}
      {hasW && (
        <text x={PAD.left - 3} y={PAD.top - 1} textAnchor="end" fontSize="7" fill="#3a6fa4">W</text>
      )}

      {/* Rechte Y-Achse: HR */}
      {hasHR && hrTicks.map(t => (
        <text key={t.v} x={PAD.left + cw + 3} y={t.y + 3} textAnchor="start" fontSize="8" fill="#c17f24">
          {t.v}
        </text>
      ))}
      {hasHR && (
        <text x={PAD.left + cw + 3} y={PAD.top - 1} textAnchor="start" fontSize="7" fill="#a06020">bpm</text>
      )}
    </svg>
  );
}

// ─── Stats-Zeile ─────────────────────────────────────────────────────────────
function StreamStats({ time, watts, heartrate, cadence }) {
  const avg = arr => {
    if (!arr) return null;
    const v = arr.filter(x => x != null);
    return v.length ? Math.round(v.reduce((a, b) => a + b, 0) / v.length) : null;
  };
  const max = arr => {
    if (!arr) return null;
    const v = arr.filter(x => x != null);
    return v.length ? Math.max(...v) : null;
  };

  const dauer = time ? Math.round(time[time.length - 1] / 60) : null;
  const avgW  = avg(watts);
  const maxW  = max(watts);
  const avgHR = avg(heartrate);
  const maxHR = max(heartrate);
  const avgCad = avg(cadence);

  const items = [
    dauer  != null && { label: 'Dauer',   val: dauer + ' min',  col: '#888' },
    avgW   != null && { label: 'Ø Watt',  val: avgW + ' W',     col: '#5a8fd4' },
    maxW   != null && { label: 'Max W',   val: maxW + ' W',     col: '#4a7fc4' },
    avgHR  != null && { label: 'Ø HR',    val: avgHR + ' bpm',  col: '#c17f24' },
    maxHR  != null && { label: 'Max HR',  val: maxHR + ' bpm',  col: '#cf7f50' },
    avgCad != null && { label: 'Ø Kad.',  val: avgCad + ' rpm', col: '#666' },
  ].filter(Boolean);

  return (
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', padding: '8px 0 4px' }}>
      {items.map(({ label, val, col }) => (
        <div key={label} style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: col }}>{val}</div>
          <div style={{ fontSize: 9, color: '#444' }}>{label}</div>
        </div>
      ))}
    </div>
  );
}

// ─── Hauptkomponente ─────────────────────────────────────────────────────────
export default function StravaChart({ activityId, onClose }) {
  const [streams, setStreams]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [fetching, setFetching] = useState(false);
  const [error, setError]       = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    apiFetch('/api/strava/streams/' + activityId)
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(setStreams)
      .catch(code => setError(code === 404 ? 'no_data' : 'fetch_error'))
      .finally(() => setLoading(false));
  }, [activityId]);

  function fetchStreams() {
    setFetching(true);
    apiFetch('/api/strava/streams/fetch/' + activityId, { method: 'POST' })
      .then(r => r.json())
      .then(() => {
        return apiFetch('/api/strava/streams/' + activityId).then(r => r.json()).then(setStreams);
      })
      .catch(() => setError('fetch_error'))
      .finally(() => { setFetching(false); setError(null); });
  }

  return (
    <div style={{ background: '#0d0d0d', borderRadius: 8, overflow: 'hidden', border: '1px solid #1a2a3a' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: '#111' }}>
        <div style={{ fontSize: 10, color: '#5a8fd4', letterSpacing: 2 }}>VERLAUF</div>
        <button onClick={onClose}
          style={{ background: 'transparent', border: 'none', color: '#444', fontSize: 16, cursor: 'pointer', lineHeight: 1, padding: '0 2px' }}>
          ×
        </button>
      </div>

      {loading && (
        <div style={{ padding: '24px 14px', textAlign: 'center', fontSize: 12, color: '#444' }}>Lade...</div>
      )}

      {!loading && error === 'no_data' && (
        <div style={{ padding: '16px 14px', display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'flex-start' }}>
          <div style={{ fontSize: 12, color: '#555' }}>Keine Stream-Daten — Aktivität wurde vor dem Stream-Import gespeichert.</div>
          <button onClick={fetchStreams} disabled={fetching}
            style={{ background: '#1a2a3a', border: '1px solid #2a4a6a', borderRadius: 5, padding: '8px 14px', color: '#5a8fd4', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>
            {fetching ? 'Lade von Strava...' : 'Streams von Strava laden'}
          </button>
        </div>
      )}

      {!loading && error === 'fetch_error' && (
        <div style={{ padding: '16px 14px', fontSize: 12, color: '#cf7f50' }}>Fehler beim Laden der Daten.</div>
      )}

      {!loading && !error && streams && (
        <div style={{ padding: '0 0 10px' }}>
          <StreamSVG time={streams.time} watts={streams.watts} heartrate={streams.heartrate} />
          <div style={{ padding: '0 14px' }}>
            <StreamStats
              time={streams.time} watts={streams.watts}
              heartrate={streams.heartrate} cadence={streams.cadence}
            />
            {/* Legende */}
            <div style={{ display: 'flex', gap: 14, marginTop: 4 }}>
              {streams.watts && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <div style={{ width: 16, height: 2, background: '#5a8fd4' }} />
                  <span style={{ fontSize: 9, color: '#5a8fd4' }}>Watt</span>
                </div>
              )}
              {streams.heartrate && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <div style={{ width: 16, height: 2, background: '#c17f24' }} />
                  <span style={{ fontSize: 9, color: '#c17f24' }}>Herzfrequenz</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
